import cn from "classnames"

import { useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPreferences, selectWeatherTimeline } from "features/map/control/selectors"
import { LightningUnit } from "features/map/control/types"
import { colormapAt } from "../shared/colormap"
import {
    LIGHTNING_FADE_IN_MAX,
    LIGHTNING_MIN_VISIBLE,
    LIGHTNING_RISK_COLOR_STOPS,
    LIGHTNING_RISK_MAX,
} from "./LightningRiskHeatmapLayer"
import {
    FLASH_ICON_RGB_DARK,
    FLASH_ICON_RGB_LIGHT,
    LIGHTNING_STRIKES_MIN_ZOOM,
    STRIKE_COLOUR_STOPS,
    STRIKE_KA_AXIS_MAX,
} from "./lightningStrikesLayers"

const RISK_TICKS = [0, 0.05, 0.15, 0.35, 0.6, 1.0] as const
const STRIKE_TICKS = [0, 15, 30, 45, 60] as const

const UNIT_CONVERTERS: Record<LightningUnit, (perKm2: number) => number> = {
    [LightningUnit.PerSquareKilometerHour]: (v) => v,
    [LightningUnit.PerSquareMileHour]: (v) => v * 2.589988110336,
}

const cssRgba = (c: readonly [number, number, number], alpha: number) =>
    `rgba(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)}, ${alpha})`

const cssRgb = (c: readonly [number, number, number]) => cssRgba(c, 1)

const pctOf = (v: number, axisMax: number) => ((v / axisMax) * 100).toFixed(2)

const RISK_GRADIENT_CSS = (() => {
    const fadeBoundaryColor = colormapAt(LIGHTNING_FADE_IN_MAX, LIGHTNING_RISK_COLOR_STOPS)
    const pct = (v: number) => pctOf(v, LIGHTNING_RISK_MAX)
    const parts = [
        `${cssRgba(LIGHTNING_RISK_COLOR_STOPS[0].color, 0)} 0%`,
        `${cssRgba(colormapAt(LIGHTNING_MIN_VISIBLE, LIGHTNING_RISK_COLOR_STOPS), 0)} ${pct(LIGHTNING_MIN_VISIBLE)}%`,
        `${cssRgb(fadeBoundaryColor)} ${pct(LIGHTNING_FADE_IN_MAX)}%`,
        ...LIGHTNING_RISK_COLOR_STOPS.filter((s) => s.value > LIGHTNING_FADE_IN_MAX).map(
            (s) => `${cssRgb(s.color)} ${pct(s.value)}%`
        ),
    ]
    return parts.join(", ")
})()

export const RISK_SWATCH_GRADIENT_CSS = LIGHTNING_RISK_COLOR_STOPS.map((s) => cssRgb(s.color)).join(
    ", "
)

const STRIKE_GRADIENT_CSS = [
    `${cssRgb(STRIKE_COLOUR_STOPS[0].color)} 0%`,
    ...STRIKE_COLOUR_STOPS.map((s) => `${cssRgb(s.color)} ${pctOf(s.value, STRIKE_KA_AXIS_MAX)}%`),
].join(", ")

const GROUND_GLYPH_PATH =
    "M14.0441 0.85714V9.95597H23.14286V14.04408H14.0441V23.14286H9.95597V14.04408H0.85714V9.95597H9.95597V0.85714H14.0441Z"

const STRIKE_GLYPH_COLOUR = cssRgb(STRIKE_COLOUR_STOPS[STRIKE_COLOUR_STOPS.length - 1].color)

export const GroundStrikeGlyph = ({ className = "h-3 w-3" }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d={GROUND_GLYPH_PATH} fill={STRIKE_GLYPH_COLOUR} />
    </svg>
)

const POTENTIAL_GLYPH_PATH =
    "M7.225 10.425C7.38611 10.2639 7.46667 10.0667 7.46667 9.83333C7.46667 9.6 7.38611 9.40278 7.225 9.24167C7.06389 9.08055 6.86667 9 6.63333 9C6.4 9 6.20278 9.08055 6.04167 9.24167C5.88056 9.40278 5.8 9.6 5.8 9.83333C5.8 10.0667 5.88056 10.2639 6.04167 10.425C6.20278 10.5861 6.4 10.6667 6.63333 10.6667C6.86667 10.6667 7.06389 10.5861 7.225 10.425ZM6.03333 8.1H7.26667C7.26667 7.73333 7.30833 7.44444 7.39167 7.23333C7.475 7.02222 7.71111 6.73333 8.1 6.36667C8.38889 6.07778 8.61667 5.80278 8.78333 5.54167C8.95 5.28056 9.03333 4.96667 9.03333 4.6C9.03333 3.97778 8.80556 3.5 8.35 3.16667C7.89444 2.83333 7.35556 2.66667 6.73333 2.66667C6.1 2.66667 5.58611 2.83333 5.19167 3.16667C4.79722 3.5 4.52222 3.9 4.36667 4.36667L5.46667 4.8C5.52222 4.6 5.64722 4.38333 5.84167 4.15C6.03611 3.91667 6.33333 3.8 6.73333 3.8C7.08889 3.8 7.35556 3.89722 7.53333 4.09167C7.71111 4.28611 7.8 4.5 7.8 4.73333C7.8 4.95556 7.73333 5.16389 7.6 5.35833C7.46667 5.55278 7.3 5.73333 7.1 5.9C6.61111 6.33333 6.31111 6.66111 6.2 6.88333C6.08889 7.10556 6.03333 7.51111 6.03333 8.1ZM6.66667 13.3333C5.74444 13.3333 4.87778 13.1583 4.06667 12.8083C3.25556 12.4583 2.55 11.9833 1.95 11.3833C1.35 10.7833 0.875 10.0778 0.525 9.26667C0.175 8.45555 0 7.58889 0 6.66667C0 5.74444 0.175 4.87778 0.525 4.06667C0.875 3.25556 1.35 2.55 1.95 1.95C2.55 1.35 3.25556 0.875 4.06667 0.525C4.87778 0.175 5.74444 0 6.66667 0C7.58889 0 8.45555 0.175 9.26667 0.525C10.0778 0.875 10.7833 1.35 11.3833 1.95C11.9833 2.55 12.4583 3.25556 12.8083 4.06667C13.1583 4.87778 13.3333 5.74444 13.3333 6.66667C13.3333 7.58889 13.1583 8.45555 12.8083 9.26667C12.4583 10.0778 11.9833 10.7833 11.3833 11.3833C10.7833 11.9833 10.0778 12.4583 9.26667 12.8083C8.45555 13.1583 7.58889 13.3333 6.66667 13.3333ZM6.66667 12C8.15555 12 9.41667 11.4833 10.45 10.45C11.4833 9.41667 12 8.15555 12 6.66667C12 5.17778 11.4833 3.91667 10.45 2.88333C9.41667 1.85 8.15555 1.33333 6.66667 1.33333C5.17778 1.33333 3.91667 1.85 2.88333 2.88333C1.85 3.91667 1.33333 5.17778 1.33333 6.66667C1.33333 8.15555 1.85 9.41667 2.88333 10.45C3.91667 11.4833 5.17778 12 6.66667 12Z"

export const PotentialStrikeGlyph = ({
    isLight,
    className = "h-3 w-3",
}: {
    isLight: boolean
    className?: string
}) => (
    <svg viewBox="0 0 14 14" className={className} aria-hidden="true">
        <path
            d={POTENTIAL_GLYPH_PATH}
            opacity={0.75}
            fill={cssRgb(isLight ? FLASH_ICON_RGB_LIGHT : FLASH_ICON_RGB_DARK)}
        />
    </svg>
)

const GradientBar = ({
    gradient,
    ticks,
    unit,
    isLight,
    textColor,
}: {
    gradient: string
    ticks: ReadonlyArray<number>
    unit: string
    isLight: boolean
    textColor: string
}) => (
    <div className="flex items-end gap-2">
        <div>
            <div
                className={cn("h-2 w-[230px] rounded border", {
                    ["bg-gray-300 border-black-500"]: isLight,
                    ["bg-gray-600 border-white-500"]: !isLight,
                })}
                style={{ background: `linear-gradient(to right, ${gradient})` }}
            />
            <div className="mt-1 flex h-3 w-[230px] justify-between">
                {ticks.map((v, i) => (
                    <span key={i} className={cn("font-mono text-[10px] leading-none", textColor)}>
                        {v}
                    </span>
                ))}
            </div>
        </div>
        <span className={cn("whitespace-nowrap text-xs leading-none", textColor)}>{unit}</span>
    </div>
)

export const LightningRiskLegend = ({ zoom, isLight }: { zoom: number; isLight: boolean }) => {
    const { lightningUnit } = useAppSelector(selectWeatherPreferences)
    const { currentTime } = useAppSelector(selectWeatherTimeline)
    const textColor = cn({ ["text-white"]: !isLight, ["text-gray-900"]: isLight })

    const strikesVisible =
        zoom >= LIGHTNING_STRIKES_MIN_ZOOM && Date.parse(currentTime) <= Date.now()

    if (strikesVisible) {
        return (
            <div className="flex flex-col gap-1.5">
                <div className={cn("flex items-center gap-3 text-[10px] leading-none", textColor)}>
                    <span className="flex items-center gap-1">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                            <GroundStrikeGlyph />
                        </span>
                        Ground Strike
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                            <PotentialStrikeGlyph isLight={isLight} />
                        </span>
                        Potential Strike
                    </span>
                </div>
                <GradientBar
                    gradient={STRIKE_GRADIENT_CSS}
                    ticks={STRIKE_TICKS}
                    unit="kA"
                    isLight={isLight}
                    textColor={textColor}
                />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-1.5">
            {zoom < LIGHTNING_STRIKES_MIN_ZOOM ? (
                <div className={cn("text-sm font-semibold leading-none", textColor)}>
                    Zoom in for strike data
                </div>
            ) : null}
            <GradientBar
                gradient={RISK_GRADIENT_CSS}
                ticks={RISK_TICKS.map((v) => Number(UNIT_CONVERTERS[lightningUnit](v).toFixed(2)))}
                unit={lightningUnit}
                isLight={isLight}
                textColor={textColor}
            />
        </div>
    )
}
